import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import {
  CACHE_DURATIONS,
  transformErrorResponse
} from './baseApi';

// Types for shared search functionality

/** Which side of the product a saved/recent search belongs to. */
export type SearchType = 'company' | 'contact';

/**
 * The filter body a search was saved with, replayed verbatim when the user
 * restores it — the same opaque, UI-driven shape buildPeopleSearchPayload /
 * buildFiltersObject assemble, which this slice does not read into.
 */
export type SavedSearchQuery = Record<string, unknown>;

/** One hit as the search service returns it; field set varies by index. */
export type SearchHit = Record<string, unknown>;

/** The deleted search's id, echoed back beside whatever body the service answered with. */
export interface DeleteSavedSearchResult {
  searchId: string;
  data: unknown;
}

export interface SavedSearch {
  id: string;
  searchName: string;
  searchType: SearchType;
  searchQuery: SavedSearchQuery;
  dateAdded?: string;
}

export interface RecentSearch {
  id: string;
  searchType: SearchType;
  searchQuery: SavedSearchQuery;
  dateAdded?: string;
}

export interface SearchResults {
  company: {
    total: number;
    hits: SearchHit[];
  };
  contact: {
    total: number;
    hits: SearchHit[];
  };
  aggregations: {
    industries?: Array<{ key: string; doc_count: number }>;
    countries?: Array<{ key: string; doc_count: number }>;
    cities?: Array<{ key: string; doc_count: number }>;
    comp_size_range?: Array<{ key: string; doc_count: number }>;
    revenue_range?: Array<{ key: string; doc_count: number }>;
    seniority?: Array<{ key: string; doc_count: number }>;
    functional_level?: Array<{ key: string; doc_count: number }>;
  };
}

export interface SearchFilters {
  industry?: string[];
  country?: string[];
  city?: string[];
  size_range?: string[];
  revenue_range?: string[];
  technology?: string[];
  seniority?: string[];
  functional_level?: string[];
}

// Search params interfaces
interface SaveSearchParams {
  searchName: string;
  searchType: SearchType;
  searchQuery: SavedSearchQuery;
}

interface GetSavedSearchesParams {
  type: SearchType;
  from?: number;
  size?: number;
}

interface GetRecentSearchesParams {
  type: SearchType;
  from?: number;
  size?: number;
}

interface CheckNameParams {
  type: SearchType;
  name: string;
}

interface AdvancedSearchParams {
  query: string;
  filters?: SearchFilters;
  size?: number;
  from?: number;
  type?: SearchType | 'both';
}

interface InsightsBucket {
  key: string;
  count: number;
  /**
   * How many of this bucket's `count` are reachable. Only present once the
   * groups endpoint sends per-bucket reach counts - absent means "not sent",
   * never zero, and the sheet draws the plain breakdown.
   */
  emailable?: number;
  phonable?: number;
  /** Companies: the same four sub-counts as the companies stats `counts`. */
  company_emailable?: number;
  company_phoneable?: number;
  contact_emailable?: number;
  contact_phoneable?: number;
}

interface InsightsGroup {
  field: string;
  buckets: InsightsBucket[];
}

/**
 * The service spells the phone count `phonable` on stats but the request flag
 * `phoneable`; accept either on buckets so the sheet only ever reads `phonable`.
 */
function normalizeGroups(groups: InsightsGroup[] | undefined): InsightsGroup[] {
  return (groups ?? []).map((group) => ({
    ...group,
    buckets: (group.buckets ?? []).map((raw) => {
      const { phoneable, ...bucket } = raw as InsightsBucket & { phoneable?: number };
      return bucket.phonable === undefined && phoneable !== undefined
        ? { ...bucket, phonable: phoneable }
        : bucket;
    }),
  }));
}

export interface DataInsights {
  /** Contacts matching the filters, before any breakdown. */
  filteredContacts: number;
  /** Companies matching the filters, before any breakdown. */
  filteredCompanies: number;
  /** Contacts in the database, ignoring the filters. */
  totalContacts: number;
  /** Companies in the database, ignoring the filters. */
  totalCompanies: number;
  /**
   * Reachability of the filtered set.
   * Contacts (`emailable`/`phonable`, the service's spelling): contacts with a
   * verified email / default direct dial.
   * Companies: the company's own email / boardline (`company_*`), and having at
   * least one contact with a verified email / phone (`contact_*`).
   */
  counts?: {
    mailable?: number;
    phonable?: number;
    emailable?: number;
    company_emailable?: number;
    company_phoneable?: number;
    contact_emailable?: number;
    contact_phoneable?: number;
  };
  groups: InsightsGroup[];
}

/** The totals/reachability half of DataInsights, from the split `/insights/stats` endpoint. */
export type DataInsightsStats = Omit<DataInsights, 'groups'>;

/**
 * The breakdown half of DataInsights, from the split `/insights/groups`
 * endpoint. `total` is the matched count for whichever entity these groups
 * describe (contacts' `filteredContacts`, companies' `total`) — the service
 * sends it alongside `groups` specifically so the share/percentage math
 * doesn't have to wait on the stats call.
 */
export interface DataInsightsGroupsData {
  total: number;
  groups: InsightsGroup[];
}

export interface DataInsightsParams {
  /**
   * The same filter object the matching search sends. Omitted or empty means
   * unfiltered — i.e. the whole database.
   */
  filters?: Record<string, unknown>;
  /**
   * Which fields to break down. Omitted means every field the endpoint
   * supports, which is what the sheet wants.
   */
  groupBy?: string[];
  /**
   * Groups only: ask for each bucket's emailable/phoneable sub-count (the 2D
   * breakdown). The service reads these off the same keys as the search's own
   * Emailable/Phoneable filters, but on the groups endpoints strips them from
   * the filter - so they add counts without narrowing. Never send them to the
   * stats endpoints, which still filter by them.
   */
  reach?: InsightsReach;
}

export interface InsightsReach {
  /** Contacts. */
  emailable?: boolean;
  phoneable?: boolean;
  /** Companies. */
  company_emailable?: boolean;
  company_phoneable?: boolean;
  contact_emailable?: boolean;
  contact_phoneable?: boolean;
}

const COMPANY_REACH_KEYS = [
  'company_emailable',
  'company_phoneable',
  'contact_emailable',
  'contact_phoneable',
] as const;

/**
 * The service reads the filters off the top level of the body and `groupBy`
 * alongside them, so the two are merged rather than nested.
 */
function buildInsightsBody(params: DataInsightsParams | void): Record<string, unknown> {
  const { filters, groupBy } = params || {};
  return {
    ...(filters || {}),
    ...(groupBy && groupBy.length > 0 ? { groupBy } : {}),
  };
}

/**
 * Contacts' reach flags (under `contact`). They only ever switch on - a search
 * that already filters on Emailable keeps its `true`.
 */
function reachFlags(reach: InsightsReach | undefined): Record<string, boolean> {
  return {
    ...(reach?.emailable ? { emailable: true } : {}),
    ...(reach?.phoneable ? { phoneable: true } : {}),
  };
}

/**
 * Companies groups reads all four Contactability keys as reach flags (never as
 * filters), so the search's own Contactability selections are dropped here -
 * otherwise they'd draw a breakdown the sheet's switches show as off.
 */
function buildCompanyGroupsBody(params: DataInsightsParams | void): Record<string, unknown> {
  const body = buildInsightsBody(params);
  for (const key of COMPANY_REACH_KEYS) {
    delete body[key];
    if (params?.reach?.[key]) body[key] = true;
  }
  return body;
}

function buildContactGroupsBody(params: DataInsightsParams | void): Record<string, unknown> {
  const body = buildInsightsBody(params);
  const flags = reachFlags(params?.reach);
  if (Object.keys(flags).length === 0) return body;
  return { ...body, contact: { ...((body.contact as Record<string, unknown>) || {}), ...flags } };
}

// Tag types for shared search data
const SEARCH_TAGS = {
  SAVED_SEARCHES: 'SavedSearches',
  RECENT_SEARCHES: 'RecentSearches',
  ADVANCED_SEARCH: 'AdvancedSearch',
  DATA_INSIGHTS: 'DataInsights',
} as const;

// Endpoints below build their own full paths (e.g. `/api/searches/save`),
// so this base query is given no path prefix of its own.
const baseSearchApi = createApi({
  reducerPath: 'searchApi',
  baseQuery: axiosBaseQuery(''),
  tagTypes: [SEARCH_TAGS.SAVED_SEARCHES, SEARCH_TAGS.RECENT_SEARCHES, SEARCH_TAGS.ADVANCED_SEARCH, SEARCH_TAGS.DATA_INSIGHTS],
  refetchOnFocus: false,
  refetchOnReconnect: true,
  keepUnusedDataFor: CACHE_DURATIONS.SHORT,
  endpoints: () => ({}),
});

export const searchApi = baseSearchApi.injectEndpoints({
  endpoints: (builder) => ({
    
    /**
     * Advanced search (supports both company and contact)
     * Cache for 3 minutes with smart duplicate prevention
     */
    advancedSearch: builder.query<SearchResults, AdvancedSearchParams>({
      query: ({ query, filters, size = 25, from = 0, type = 'both' }) => ({
        url: `/api/search/advanced`,
        method: 'POST',
        body: { query, filters, size, from, type },
      }),
      
      // Cache for 3 minutes
      keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT * 3,
      
      // Force cache reuse for identical requests
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      
      providesTags: (_result, _error, params) => {
        const cacheKey = JSON.stringify(params);
        return [
          SEARCH_TAGS.ADVANCED_SEARCH,
          { type: SEARCH_TAGS.ADVANCED_SEARCH, id: cacheKey }
        ];
      },

      transformResponse: (response: unknown): SearchResults => {
        const body = (response ?? {}) as Partial<SearchResults>;
        return {
          company: {
            total: body.company?.total || 0,
            hits: body.company?.hits || []
          },
          contact: {
            total: body.contact?.total || 0,
            hits: body.contact?.hits || []
          },
          aggregations: body.aggregations || {}
        };
      },

      transformErrorResponse,
    }),

    /**
     * Save search filter
     * Invalidate saved searches list after saving
     * POST /api/searches/save
     * Body: { searchName, searchType, searchQuery }
     */
    saveSearchFilter: builder.mutation<SavedSearch, SaveSearchParams>({
      query: (searchData) => ({
        url: `/api/searches/save`,
        method: 'POST',
        body: searchData,
      }),
      
      invalidatesTags: (_result, _error, { searchType }) => [
        { type: SEARCH_TAGS.SAVED_SEARCHES, id: `${searchType}` },
        { type: SEARCH_TAGS.SAVED_SEARCHES, id: 'LIST' }
      ],

      transformErrorResponse,
    }),

    /**
     * Get saved searches
     * Cache for 5 minutes since they don't change often
     * GET /api/searches/saved?type=contact&from=0&size=25
     */
    getSavedSearches: builder.query<SavedSearch[], GetSavedSearchesParams>({
      query: ({ type, from = 0, size = 25 }) => ({
        url: `/api/searches/saved`,
        params: { type, from, size },
      }),
      
      keepUnusedDataFor: CACHE_DURATIONS.SHORT,
      
      providesTags: (result, _error, { type }) => [
        SEARCH_TAGS.SAVED_SEARCHES,
        { type: SEARCH_TAGS.SAVED_SEARCHES, id: `${type}` },
        { type: SEARCH_TAGS.SAVED_SEARCHES, id: 'LIST' },
        ...(result?.map(search =>
          ({ type: SEARCH_TAGS.SAVED_SEARCHES, id: search.id })
        ) || [])
      ],

      // The service responds with { total, from, size, results: [...] },
      // not a bare array or a `data` key.
      transformResponse: (response: unknown): SavedSearch[] => {
        if (Array.isArray(response)) return response as SavedSearch[];
        return (response as { results?: SavedSearch[] } | null)?.results || [];
      },

      transformErrorResponse,
    }),

    /**
     * How many saved searches of one type the account holds.
     *
     * The same endpoint as getSavedSearches, asked for a single row so the
     * response's `total` can be read without paging. It exists separately
     * because that transform deliberately returns a bare array — every
     * consumer wants the rows — while the `savedSearch` entitlement needs the
     * COUNT, and it needs the true one rather than the length of a 25-row
     * page. `usageSource` on that feature is "caller", meaning the credit
     * service cannot see this number and expects it to be passed in.
     *
     * GET /api/searches/saved?type=contact&from=0&size=1
     */
    getSavedSearchCount: builder.query<number, { type: SearchType }>({
      query: ({ type }) => ({
        url: `/api/searches/saved`,
        params: { type, from: 0, size: 1 },
      }),

      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      providesTags: (_result, _error, { type }) => [
        SEARCH_TAGS.SAVED_SEARCHES,
        { type: SEARCH_TAGS.SAVED_SEARCHES, id: `${type}` },
        { type: SEARCH_TAGS.SAVED_SEARCHES, id: 'LIST' },
      ],

      // An older shape returned a bare array, in which case its length is the
      // only count available - which is right whenever it is under one page.
      transformResponse: (response: unknown): number => {
        if (Array.isArray(response)) return response.length;
        const body = response as { total?: number; results?: unknown[] } | null;
        return body?.total ?? body?.results?.length ?? 0;
      },

      transformErrorResponse,
    }),

    /**
     * Delete saved search
     * Invalidate saved searches list after deletion
     * DELETE /api/searches/saved?searchId=xxx
     */
    deleteSavedSearch: builder.mutation<DeleteSavedSearchResult, string>({
      query: (searchId) => ({
        url: `/api/searches/saved`,
        method: 'DELETE',
        params: { searchId },
      }),

      invalidatesTags: (_result, _error, searchId) => [
        { type: SEARCH_TAGS.SAVED_SEARCHES, id: searchId },
        { type: SEARCH_TAGS.SAVED_SEARCHES, id: 'LIST' }
      ],

      // The id is echoed back alongside the body so the caller can tell which
      // row a completed delete belongs to without closing over it.
      transformResponse: (response: unknown, _meta, searchId): DeleteSavedSearchResult => ({
        searchId,
        data: response,
      }),

      transformErrorResponse,
    }),

    /**
     * Get recent searches
     * Cache for 2 minutes since they change frequently
     * GET /api/searches/recent?type=contact&from=0&size=25
     */
    getRecentSearches: builder.query<RecentSearch[], GetRecentSearchesParams>({
      query: ({ type, from = 0, size = 25 }) => ({
        url: `/api/searches/recent`,
        params: { type, from, size },
      }),
      
      keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT * 2,
      
      providesTags: (_result, _error, { type }) => [
        SEARCH_TAGS.RECENT_SEARCHES,
        { type: SEARCH_TAGS.RECENT_SEARCHES, id: `${type}` }
      ],

      // The service responds with { total, from, size, results: [...] }, and
      // each result's timestamp field is `createdAt`, not `dateAdded`.
      transformResponse: (response: unknown): RecentSearch[] => {
        const results: Array<RecentSearch & { createdAt?: string }> = Array.isArray(response)
          ? response
          : (response as { results?: Array<RecentSearch & { createdAt?: string }> } | null)?.results || [];
        return results.map((item) => ({
          ...item,
          dateAdded: item.dateAdded || item.createdAt,
        }));
      },

      transformErrorResponse,
    }),

    /**
     * Check if saved filter name is unique
     * GET /api/searches/check-name?type=contact&name=xxx
     */
    checkFilterName: builder.query<boolean, CheckNameParams>({
      query: ({ type, name }) => ({
        url: `/api/searches/check-name`,
        params: { type, name },
      }),

      // short cache, UI will debounce before calling
      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      // API returns { exists: boolean }
      transformResponse: (response: unknown): boolean =>
        Boolean((response as { exists?: boolean } | null)?.exists),

      transformErrorResponse,
    }),

    /**
     * Companies insights for the filters currently applied.
     *
     * POST /api/data-insights/companies — the body is the same filter object
     * the company search sends, so the breakdown describes the result set the
     * user is looking at. Fields: industry, companySize, revenue, city, state,
     * country (top 10 each).
     */
    getCompaniesInsights: builder.query<DataInsights, DataInsightsParams | void>({
      query: (params) => ({
        url: `/api/data-insights/companies`,
        method: 'POST',
        body: buildInsightsBody(params),
      }),

      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      providesTags: [SEARCH_TAGS.DATA_INSIGHTS, { type: SEARCH_TAGS.DATA_INSIGHTS, id: 'companies' }],

      transformResponse: (response: unknown): DataInsights => {
        const body = (response ?? {}) as DataInsights;
        // Upstream may return { total, groups } (legacy) or full DataInsights shape
        if (body.filteredCompanies !== undefined || body.filteredContacts !== undefined) {
          return body;
        }
        const legacyTotal = (body as { total?: number }).total ?? 0;
        return {
          filteredCompanies: legacyTotal,
          filteredContacts: 0,
          totalCompanies: legacyTotal,
          totalContacts: 0,
          groups: body.groups ?? [],
        };
      },

      transformErrorResponse,
    }),

    /**
     * Contacts insights for the filters currently applied.
     *
     * POST /api/data-insights/contacts — the body is the same filter object
     * the people search sends. Fields: seniority, functionalLevel, industry,
     * companySize, revenue, city, state, country (top 10 each).
     */
    getContactsInsights: builder.query<DataInsights, DataInsightsParams | void>({
      query: (params) => ({
        url: `/api/data-insights/contacts`,
        method: 'POST',
        body: buildInsightsBody(params),
      }),

      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      providesTags: [SEARCH_TAGS.DATA_INSIGHTS, { type: SEARCH_TAGS.DATA_INSIGHTS, id: 'contacts' }],

      transformResponse: (response: unknown): DataInsights => {
        const body = (response ?? {}) as DataInsights;
        if (body.filteredContacts !== undefined || body.filteredCompanies !== undefined) {
          return body;
        }
        const legacyTotal = (body as { total?: number }).total ?? 0;
        return {
          filteredContacts: legacyTotal,
          filteredCompanies: 0,
          totalContacts: legacyTotal,
          totalCompanies: 0,
          groups: body.groups ?? [],
        };
      },

      transformErrorResponse,
    }),

    /**
     * Companies insights, stats half only — POST /api/data-insights/companies/stats.
     * Fired in parallel with getCompaniesInsightsGroups so the stat cards render
     * as soon as this resolves, without waiting on the (often slower) breakdown.
     */
    getCompaniesInsightsStats: builder.query<DataInsightsStats, DataInsightsParams | void>({
      query: (params) => ({
        url: `/api/data-insights/companies/stats`,
        method: 'POST',
        body: buildInsightsBody(params),
      }),

      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      providesTags: [
        SEARCH_TAGS.DATA_INSIGHTS,
        { type: SEARCH_TAGS.DATA_INSIGHTS, id: 'companies-stats' },
      ],

      transformResponse: (response: unknown): DataInsightsStats => {
        const body = (response ?? {}) as Partial<DataInsightsStats> & { total?: number };
        // Companies stats is { total, counts }: the count matching the current
        // filters plus its reachability. total is mirrored onto totalCompanies
        // too — the same placeholder the combined endpoint has always used,
        // pending a real "in the database" figure the service doesn't send for
        // companies.
        if (body.filteredCompanies === undefined && body.total !== undefined) {
          return {
            filteredCompanies: body.total,
            totalCompanies: body.total,
            filteredContacts: 0,
            totalContacts: 0,
            counts: body.counts,
          };
        }
        return {
          filteredCompanies: body.filteredCompanies ?? 0,
          totalCompanies: body.totalCompanies ?? 0,
          filteredContacts: body.filteredContacts ?? 0,
          totalContacts: body.totalContacts ?? 0,
          counts: body.counts,
        };
      },

      transformErrorResponse,
    }),

    /**
     * Companies insights, breakdown half only — POST /api/data-insights/companies/groups.
     */
    getCompaniesInsightsGroups: builder.query<DataInsightsGroupsData, DataInsightsParams | void>({
      query: (params) => ({
        url: `/api/data-insights/companies/groups`,
        method: 'POST',
        body: buildCompanyGroupsBody(params),
      }),

      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      providesTags: [
        SEARCH_TAGS.DATA_INSIGHTS,
        { type: SEARCH_TAGS.DATA_INSIGHTS, id: 'companies-groups' },
      ],

      transformResponse: (response: unknown): DataInsightsGroupsData => {
        const body = (response ?? {}) as { total?: number; groups?: InsightsGroup[] };
        return { total: body.total ?? 0, groups: normalizeGroups(body.groups) };
      },

      transformErrorResponse,
    }),

    /**
     * Contacts insights, stats half only — POST /api/data-insights/contacts/stats.
     */
    getContactsInsightsStats: builder.query<DataInsightsStats, DataInsightsParams | void>({
      query: (params) => ({
        url: `/api/data-insights/contacts/stats`,
        method: 'POST',
        body: buildInsightsBody(params),
      }),

      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      providesTags: [
        SEARCH_TAGS.DATA_INSIGHTS,
        { type: SEARCH_TAGS.DATA_INSIGHTS, id: 'contacts-stats' },
      ],

      transformResponse: (response: unknown): DataInsightsStats => {
        const body = (response ?? {}) as Partial<DataInsightsStats>;
        return {
          filteredContacts: body.filteredContacts ?? 0,
          filteredCompanies: body.filteredCompanies ?? 0,
          totalContacts: body.totalContacts ?? 0,
          totalCompanies: body.totalCompanies ?? 0,
          counts: body.counts,
        };
      },

      transformErrorResponse,
    }),

    /**
     * Contacts insights, breakdown half only — POST /api/data-insights/contacts/groups.
     */
    getContactsInsightsGroups: builder.query<DataInsightsGroupsData, DataInsightsParams | void>({
      query: (params) => ({
        url: `/api/data-insights/contacts/groups`,
        method: 'POST',
        body: buildContactGroupsBody(params),
      }),

      keepUnusedDataFor: CACHE_DURATIONS.SHORT,

      providesTags: [
        SEARCH_TAGS.DATA_INSIGHTS,
        { type: SEARCH_TAGS.DATA_INSIGHTS, id: 'contacts-groups' },
      ],

      transformResponse: (response: unknown): DataInsightsGroupsData => {
        const body = (response ?? {}) as { filteredContacts?: number; groups?: InsightsGroup[] };
        // Contacts groups carries `filteredContacts` alongside `groups` (unlike
        // companies' `total`) — that's the count these buckets' shares are
        // taken against, so it doesn't need the stats call to render.
        return { total: body.filteredContacts ?? 0, groups: normalizeGroups(body.groups) };
      },

      transformErrorResponse,
    }),
  }),
});

// Export hooks for use in components
export const {
  useAdvancedSearchQuery,
  useSaveSearchFilterMutation,
  useLazyGetSavedSearchesQuery,
  useGetSavedSearchCountQuery,
  useDeleteSavedSearchMutation,
  useLazyGetRecentSearchesQuery,
  useCheckFilterNameQuery,
  useGetCompaniesInsightsQuery,
  useGetContactsInsightsQuery,
  useGetCompaniesInsightsStatsQuery,
  useGetCompaniesInsightsGroupsQuery,
  useGetContactsInsightsStatsQuery,
  useGetContactsInsightsGroupsQuery,
} = searchApi;

// Export for store configuration
export default searchApi;